import { ComponentProps, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { InView } from 'react-intersection-observer'
import { useUpdateEffect } from 'react-use'
import styled from 'styled-components'

import { Media, TypeStyle, fromLg, fromMd, untilMd } from '@syconium/little-miss-figgy'

import { ContentfulPageTileSection } from '../../../../app/_components/pages/contentful/sections/ContentfulPageTileSection.client'
import { ContentfulPageVideoSection } from '../../../../app/_components/pages/contentful/sections/ContentfulPageVideoSection.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { usePagesDirectoryStableQueryParams } from '../../../../lib/adapters/next-routing-service/useUpdateQueryParam'
import {
  CollectionFilterQueryParamsContextProvider,
  useCollectionFilterQueryParamsContext,
} from '../../../containers/CollectionFilterQueryParamsContext'

import {
  CollectionGridSectionFiltersSkeleton,
  CollectionGridSectionTilesSkeleton,
} from './CollectionPageSkeleton'
import { DesktopFilterBarSkeleton } from './CollectionPageSkeleton/DesktopFilterBarSkeleton'
import { useConditionallyLoadMoreItems } from './hooks/useConditionallyLoadMoreItems'
import {
  CollectionFiltersContext,
  useCollectionFiltersContext,
} from './sections/CollectionGridSection/containers/CollectionFiltersContext'
import { ColumnLayoutService } from './sections/CollectionGridSection/containers/ColumnLayoutService'
import { FilterModalService } from './sections/CollectionGridSection/containers/FilterModalService'
import { DesktopFilterBar } from './sections/CollectionGridSection/DesktopFilterBar'
import { VisualFilters as ContentfulVisualFilters } from './sections/CollectionGridSection/DesktopFilterBar/VisualFiltersSection/VisualFilters'
import { FilterModalLoader } from './sections/CollectionGridSection/FilterModal'
import { FilterPanel } from './sections/CollectionGridSection/FilterPanel'
import {
  FilterBarMedia,
  FilterBarWrap,
  FilterColumn,
} from './sections/CollectionGridSection/styles'
import { EditorialCell } from './sections/CollectionGridSection/Tiles/EditorialCell'
import { ProductGroupTile } from './sections/CollectionGridSection/Tiles/ProductGroupTile'
import { ProductGroupTileErrorBoundary } from './sections/CollectionGridSection/Tiles/ProductGroupTile/ProductGroupTileErrorBoundary'
import { TileItem } from './sections/CollectionGridSection/Tiles/types'
import { BackToTopButton, BackToTopText, StyledFigsLoader } from './styles'

const Providers = ({
  collectionHandle,
  totalItemsCount,
  filters,
  filterConfig,
  children,
}: {
  collectionHandle: string
  totalItemsCount: number
  filters: NonNullable<ComponentProps<typeof CollectionFiltersContext.Provider>['value']>['filters']
  filterConfig: NonNullable<
    ComponentProps<typeof CollectionFiltersContext.Provider>['value']
  >['filterConfig']
  children: ReactNode
}) => {
  return (
    <ColumnLayoutService.Provider>
      <CollectionFilterQueryParamsContextProvider
        collectionHandle={collectionHandle}
        useQueryParamsImpl={usePagesDirectoryStableQueryParams}
      >
        <CollectionFiltersContext.Provider value={{ filters, totalItemsCount, filterConfig }}>
          <FilterModalService.Provider>{children}</FilterModalService.Provider>
        </CollectionFiltersContext.Provider>
      </CollectionFilterQueryParamsContextProvider>
    </ColumnLayoutService.Provider>
  )
}

const VisualFilters = (props: ComponentProps<typeof ContentfulVisualFilters>) => {
  return <ContentfulVisualFilters {...props} />
}

const Controls = ({ isLoading }: { isLoading: boolean }) => {
  const { filters } = useCollectionFiltersContext()

  return (
    <>
      {/** Desktop filter selection dropdowns.  */}
      {isLoading || (filters && filters.length > 0) ? (
        <FilterBarMedia greaterThanOrEqual='md'>
          <FilterBarWrap>
            {isLoading ? <DesktopFilterBarSkeleton /> : <DesktopFilterBar />}
          </FilterBarWrap>
        </FilterBarMedia>
      ) : null}

      <Media lessThan='md'>
        {isLoading ? (
          <CollectionGridSectionFiltersSkeleton />
        ) : (
          <FilterColumn desktop={2.5}>
            <FilterPanel />
          </FilterColumn>
        )}
      </Media>

      {/** Mobile filter modal  */}
      {filters ? <FilterModalLoader /> : null}
    </>
  )
}

const StyledGrid = styled.ul<{
  columns: number
  columnsFromMd: number
  hasVerticalMarginDesktop?: boolean
}>`
  display: grid;
  row-gap: ${o => o.theme.spacing(6)};
  column-gap: ${o => o.theme.spacing(2)};

  ${untilMd} {
    column-gap: ${o => o.theme.spacing(1)};
  }

  grid-template-columns: ${o =>
    Array(o.columns)
      .fill(1)
      .map(() => ' 1fr')};
  ${fromMd} {
    grid-template-columns: ${o =>
      Array(o.columnsFromMd)
        .fill(1)
        .map(() => ' 1fr')};
    ${fromMd} {
      padding-top: ${o => (o.hasVerticalMarginDesktop ? o.theme.spacing(5) : 0)};
    }
  }
  ${fromLg} {
    row-gap: ${o => o.theme.spacing(10)};
  }
`

const Grid = ({
  hasVerticalMarginDesktop,
  isLoading,
  children,
}: {
  hasVerticalMarginDesktop?: boolean
  isLoading: boolean
  children: ReactNode
}) => {
  const gridWrapperRef = useRef<HTMLDivElement>(null)
  const { numberOfColumnsOnDesktop, numberOfColumnsOnMobile } = ColumnLayoutService.useContainer()
  const { selectedFilters } = useCollectionFilterQueryParamsContext()

  useUpdateEffect(() => {
    if (!gridWrapperRef.current) return

    // Scroll to top when we have new selected filters.
    // Also scroll to top when the column count is toggled.
    // Only scroll if we are not already close to our ideal destination.

    const elementRect = gridWrapperRef.current.getBoundingClientRect()
    const elementTop = elementRect.top
    const viewportHeight = Math.min(
      // TODO: Fix this auto-generated suppression.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      document.documentElement.clientHeight ?? 0,
      // TODO: Fix this auto-generated suppression.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      window.innerHeight ?? 0
    )

    const idealElementTop = viewportHeight * 0.1
    const idealScrollPosition = elementTop + window.scrollY - idealElementTop
    const scrollShouldHappen = elementTop < idealElementTop || elementTop >= viewportHeight * 0.7

    if (scrollShouldHappen) {
      window.scrollTo({
        top: idealScrollPosition,
        behavior: 'smooth',
      })
    }
  }, [Object.values(selectedFilters).join(), numberOfColumnsOnMobile, numberOfColumnsOnDesktop])

  // We use a div wrapper to ensure that we have a consistent element that is retained across loading states. So that the scroll effect always has an element to reference for scrolling regardless of loading state.
  return (
    <div ref={gridWrapperRef}>
      {isLoading ? (
        <CollectionGridSectionTilesSkeleton />
      ) : (
        <StyledGrid
          aria-label='List of products'
          hasVerticalMarginDesktop={hasVerticalMarginDesktop}
          columns={numberOfColumnsOnMobile}
          columnsFromMd={numberOfColumnsOnDesktop}
        >
          {children}
        </StyledGrid>
      )}
    </div>
  )
}

export const StyledGridItem = styled.li<{
  columnWidth: number
  columnWidthFromMd: number
  columns: number
  columnsFromMd: number
}>`
  grid-column: span min(${o => o.columnWidth}, ${o => o.columns});
  ${fromMd} {
    grid-column: span min(${o => o.columnWidthFromMd}, ${o => o.columnsFromMd});
  }
`

const StyledTitleTileContentWrapper = styled.div`
  margin-bottom: ${o => o.theme.spacing(5)};
  margin-top: ${o => o.theme.spacing(5)};

  ${untilMd} {
    margin-bottom: ${o => o.theme.spacing(4)};
    padding-left: ${o => o.theme.spacing(4)};
    padding-right: ${o => o.theme.spacing(4)};
  }
`

const StyledTitleTileSubtitleWrapper = styled.div`
  margin-top: ${o => o.theme.spacing(2)};
`

const GridItem = ({ item, index }: { item: TileItem; index: number }) => {
  const isCriticalTile = index < 4
  const { filters } = useCollectionFiltersContext()
  const { selectedFilters } = useCollectionFilterQueryParamsContext()
  const { numberOfColumnsOnMobile, numberOfColumnsOnDesktop } = ColumnLayoutService.useContainer()

  if (item.__typename === 'EditorialCellPlacement') {
    if (!item.editorialCell) return null
    if (Object.keys(selectedFilters).length > 0 && filters) return null
    return (
      <StyledGridItem
        columnWidth={numberOfColumnsOnMobile}
        // TODO: Fix this auto-generated suppression.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        columnWidthFromMd={item?.cellWidth ?? 1}
        columns={numberOfColumnsOnMobile}
        columnsFromMd={numberOfColumnsOnDesktop}
      >
        <EditorialCell
          data-testid={`tile-${index}`}
          data-test-type='editorial-cell'
          isCriticalCell={isCriticalTile}
          {...item.editorialCell}
          // TODO: Fix this auto-generated suppression.
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          cellWidth={item?.cellWidth}
        />
      </StyledGridItem>
    )
  } else if (item.__typename === 'TitleTile') {
    return (
      <StyledGridItem
        columnWidth={numberOfColumnsOnMobile}
        columnWidthFromMd={numberOfColumnsOnDesktop}
        columns={numberOfColumnsOnMobile}
        columnsFromMd={numberOfColumnsOnDesktop}
      >
        <StyledTitleTileContentWrapper>
          <TypeStyle.HeadlineSmall asTag='h3'>{item.title}</TypeStyle.HeadlineSmall>
          {item.subtitle ? (
            <StyledTitleTileSubtitleWrapper>
              <TypeStyle.BodyDefault asTag='p'>{item.subtitle}</TypeStyle.BodyDefault>
            </StyledTitleTileSubtitleWrapper>
          ) : null}
        </StyledTitleTileContentWrapper>
      </StyledGridItem>
    )
  } else if (item.__typename === 'VideoSection') {
    if (Object.keys(selectedFilters).length > 0 && filters) return null
    return (
      <StyledGridItem
        columnWidth={numberOfColumnsOnMobile}
        columnWidthFromMd={numberOfColumnsOnDesktop}
        columns={numberOfColumnsOnMobile}
        columnsFromMd={numberOfColumnsOnDesktop}
      >
        <ContentfulPageVideoSection
          data-testid={`tile-${index}`}
          data-test-type='editorial-row'
          data={item}
          pageSectionIndex={isCriticalTile ? 0 : 2}
        />
      </StyledGridItem>
    )
  } else if (item.__typename === 'EditorialRowPlacement') {
    if (Object.keys(selectedFilters).length > 0 && filters) return null

    const { editorialContent } = item
    if (editorialContent.__typename === 'VideoSection') {
      return (
        <StyledGridItem
          columnWidth={numberOfColumnsOnMobile}
          columnWidthFromMd={numberOfColumnsOnDesktop}
          columns={numberOfColumnsOnMobile}
          columnsFromMd={numberOfColumnsOnDesktop}
        >
          <ContentfulPageVideoSection
            data-testid={`tile-${index}`}
            data-test-type='editorial-row'
            data={editorialContent}
            pageSectionIndex={isCriticalTile ? 0 : 2}
          />
        </StyledGridItem>
      )
    } else {
      return (
        <StyledGridItem
          columnWidth={numberOfColumnsOnMobile}
          columnWidthFromMd={numberOfColumnsOnDesktop}
          columns={numberOfColumnsOnMobile}
          columnsFromMd={numberOfColumnsOnDesktop}
        >
          <ContentfulPageTileSection
            data-testid={`tile-${index}`}
            data-test-type='editorial-row'
            data={editorialContent}
            pageSectionIndex={isCriticalTile ? 0 : 2}
            shouldUseCollectionStyling
          />
        </StyledGridItem>
      )
    }
  } else {
    return (
      <StyledGridItem
        columnWidth={1}
        columnWidthFromMd={1}
        columns={numberOfColumnsOnMobile}
        columnsFromMd={numberOfColumnsOnDesktop}
      >
        <ProductGroupTileErrorBoundary>
          <ProductGroupTile
            data-testid={`tile-${index}`}
            data-test-type='product-tile'
            tileVariantGroup={item}
            index={index}
          />
        </ProductGroupTileErrorBoundary>
      </StyledGridItem>
    )
  }
}

const Footer = ({ loadMore }: { loadMore: (() => Promise<void>) | undefined }) => {
  const [isInView, setIsInView] = useState(false)
  const [loadAgainCount, setLoadAgainCount] = useState(0)
  const { conditionallyLoadMoreItems, isLoadingMoreItems } = useConditionallyLoadMoreItems({
    loadMoreItems: loadMore,
  })

  const {
    magnolia: { general: generalStrings },
  } = useTranslation()

  const scrollToTop = useCallback(() => {
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch {}
  }, [])

  useEffect(() => {
    let effectStatus = {
      isCurrent: true,
    }

    if (isInView) {
      conditionallyLoadMoreItems().then(() => {
        setTimeout(() => {
          // TODO: Fix this auto-generated suppression.
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (isInView && effectStatus.isCurrent) {
            setLoadAgainCount(loadAgainCount + 1)
          }
        }, 250)
      })
    }

    return () => {
      effectStatus.isCurrent = false
    }
  }, [conditionallyLoadMoreItems, isInView, loadAgainCount])

  return (
    <>
      {isLoadingMoreItems && <StyledFigsLoader />}
      <InView
        // Lets the ScrollRestorer cap programmatic scrolling at this sentinel
        // so restoring a deep position cannot jump past it and leave it
        // untriggered above the viewport.
        // FIXME: Remove when ScrollRestorer is removed.
        data-scroll-restoration-boundary
        rootMargin='0px 0px 750px 0px'
        onChange={isVisible => {
          setIsInView(isVisible)
        }}
      />
      <BackToTopButton onClick={scrollToTop}>
        <BackToTopText>{generalStrings.backToTop}</BackToTopText>
      </BackToTopButton>
    </>
  )
}

export const Collection = {
  Wrapper: Providers,
  VisualFilters,
  Controls,
  Grid,
  GridItem,
  Footer,
}
